import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useAttachmentStaging } from '@/hooks/useAttachmentStaging';
import { createPortal } from 'react-dom';
import { GenericForm, GenericFormField, GenericSelect } from '@/styles/GenericForm';
import { StandardButton, CloseButton, SecondaryButton } from '@/styles/StandardButtons';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import '@/design-system/typography.css';
import '@/design-system/forms.css';
import countries from '@/lib/countries';
import { useUser } from '@/contexts/UserContext';
import AddressAutocomplete, { AddressSelection } from '@/features/project/info/AddressAutocomplete';
import { cn } from '@/lib/utils';
import { colors } from '@/design-system/colors';
import { GenericAttachmentsSection, GenericAttachment } from '@/styles/GenericAttachmentsSection';
import { uploadVendorAttachments, deleteVendorAttachment, fetchVendorAttachments, VendorAttachment, VendorDocument, fetchVendorDocuments, uploadVendorDocument, updateVendorDocument, deleteVendorDocument } from '../store';
import { Plus, X, FileText, Calendar, Eye, Trash2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { useCostCodes, operationalCodes, costCodeLabel } from '@/lib/cost-codes';
import { fetchInvitationStatus, sendCommercialInvitation, noticeAfterSend, statusLabel, type InvitationNotice, type InvitationState } from '../../commercial-invitation';

interface Vendor {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company_name: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zip_code: string;
  is_active: boolean;
  notes: string;
  cost_code_ids?: string[];
  created_at: string;
}

interface VendorFormProps {
  isOpen: boolean;
  vendor: Vendor | null;
  onClose: () => void;
  onSave: (data: any) => Promise<string | null>; // Return vendor ID
  /** Shown by the page after "Send Invitation" (the form closes once the contact is saved). */
  onInvitation?: (notice: InvitationNotice) => void;
  error?: string | null;
}

export function VendorForm({
  isOpen,
  vendor,
  onClose,
  onSave,
  onInvitation,
  error: externalError,
}: VendorFormProps) {
  const { companyId, user } = useUser();
  const [invitation, setInvitation] = useState<InvitationState | null>(null);
  useEffect(() => {
    setInvitation(null);
    if (!isOpen || !vendor?.id) return;
    let cancelled = false;
    fetchInvitationStatus(vendor.id).then((state) => { if (!cancelled) setInvitation(state); }).catch(() => { /* no status shown: the button still works */ });
    return () => { cancelled = true; };
  }, [isOpen, vendor?.id]);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const uploadVendorAttachmentsFn = useCallback(async (id: string, files: File[]): Promise<GenericAttachment[]> => {
    if (!companyId || !user?.id) return [];
    return uploadVendorAttachments(id, files, companyId, user.id);
  }, [companyId, user]);
  const deleteVendorAttachmentFn = useCallback((id: string) => {
    if (!user?.id) return Promise.resolve(false);
    return deleteVendorAttachment(id, user.id);
  }, [user]);
  const {
    pendingFiles,
    uploadedFiles: attachments,
    setUploadedFiles: setAttachments,
    handleUpload: handleFileUpload,
    handleRemovePending,
    handleDelete: handleFileDelete,
    resetPending: resetPendingFiles,
  } = useAttachmentStaging<GenericAttachment>({
    entityId: vendor?.id,
    uploadFn: uploadVendorAttachmentsFn,
    deleteFn: deleteVendorAttachmentFn,
    newFilesPosition: 'prepend',
    onError: setError,
  });
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
  const [documents, setDocuments] = useState<VendorDocument[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const initialValuesRef = useRef<Record<string, any>>({});
  const initialDocumentsRef = useRef<VendorDocument[]>([]);
  const previousIsOpenRef = useRef<boolean>(false);
  const previousVendorIdRef = useRef<string | null>(null);

  // Initialize form data when form opens or vendor changes
  useEffect(() => {
    const vendorId = vendor?.id || null;
    const isOpening = isOpen && !previousIsOpenRef.current;
    const vendorChanged = vendorId !== previousVendorIdRef.current;
    
    if (isOpen && (isOpening || vendorChanged)) {
      const initialData = vendor ? {
        first_name: vendor.first_name || '',
        last_name: vendor.last_name || '',
        email: vendor.email || '',
        phone: vendor.phone || '',
        company_name: vendor.company_name || '',
        address: vendor.address || '',
        city: vendor.city || '',
        state: vendor.state || '',
        country: vendor.country || '',
        zip_code: vendor.zip_code || '',
        is_active: vendor.is_active !== undefined ? (vendor.is_active ? 'active' : 'inactive') : 'active',
        notes: vendor.notes || '',
        cost_code_ids: vendor.cost_code_ids || [],
      } : {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        company_name: '',
        address: '',
        city: '',
        state: '',
        country: '',
        zip_code: '',
        is_active: 'active',
        notes: '',
        cost_code_ids: [],
      };

      const deepCopy = JSON.parse(JSON.stringify(initialData));
      initialValuesRef.current = deepCopy;
      setFormData(deepCopy);
      setError(null);

      // Load attachments and documents if editing
      if (vendor && companyId) {
        // Load attachments (without expiry)
        setIsLoadingAttachments(true);
        fetchVendorAttachments(vendor.id, companyId)
          .then((fetchedAttachments) => {
            const genericAttachments: GenericAttachment[] = fetchedAttachments.map((att) => ({
              id: att.id,
              file_name: att.file_name,
              file_type: att.file_type,
              file_size: att.file_size,
              file_url: att.file_url,
              created_at: att.created_at,
              created_by: att.created_by,
              created_by_user: att.created_by_user,
            }));
            setAttachments(genericAttachments);
          })
          .catch((err) => {
            console.error('Error fetching attachments:', err);
            setAttachments([]);
          })
          .finally(() => {
            setIsLoadingAttachments(false);
          });

        // Load documents (with expiry)
        setIsLoadingDocuments(true);
        fetchVendorDocuments(vendor.id, companyId)
          .then((fetchedDocuments) => {
            setDocuments(fetchedDocuments);
            initialDocumentsRef.current = JSON.parse(JSON.stringify(fetchedDocuments));
          })
          .catch((err) => {
            console.error('Error fetching documents:', err);
            setDocuments([]);
          })
          .finally(() => {
            setIsLoadingDocuments(false);
          });
      } else {
        setAttachments([]);
        resetPendingFiles();
        setDocuments([]);
        initialDocumentsRef.current = [];
      }
    }
    
    previousIsOpenRef.current = isOpen;
    previousVendorIdRef.current = vendorId;
  }, [isOpen, vendor, companyId, setAttachments, resetPendingFiles]);


  // Update error from external prop
  useEffect(() => {
    if (externalError) {
      setError(externalError);
    }
  }, [externalError]);

  // Normalize function for comparison
  const normalize = (value: any): string => {
    if (value == null) return '';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return String(value).trim();
  };

  // Check if form has changed
  const hasFormChanged = (current: Record<string, any>, initial: Record<string, any>): boolean => {
    const currentKeys = Object.keys(current);
    const initialKeys = Object.keys(initial);
    const allKeysSet = new Set<string>();
    currentKeys.forEach(key => allKeysSet.add(key));
    initialKeys.forEach(key => allKeysSet.add(key));
    const allKeys = Array.from(allKeysSet);

    for (const key of allKeys) {
      const currentValue = normalize(current[key]);
      const initialValue = normalize(initial[key]);
      if (currentValue !== initialValue) {
        return true;
      }
    }
    return false;
  };

  // Check if attachments have changed (simplified - just check if pending files exist)
  const hasAttachmentsChanged = (): boolean => {
    return pendingFiles.length > 0;
  };

  // Check if documents have changed
  const hasDocumentsChanged = (): boolean => {
    const currentDocs = JSON.stringify(documents.map(doc => ({
      id: doc.id,
      name: doc.name,
      hasExpiry: doc.hasExpiry,
      expiryDate: doc.expiryDate,
      uploadedFileId: doc.uploadedFileId,
      file: doc.file ? doc.file.name : null,
    })));
    const initialDocs = JSON.stringify(initialDocumentsRef.current.map(doc => ({
      id: doc.id,
      name: doc.name,
      hasExpiry: doc.hasExpiry,
      expiryDate: doc.expiryDate,
      uploadedFileId: doc.uploadedFileId,
      file: null,
    })));
    return currentDocs !== initialDocs;
  };

  const handleCloseAttempt = () => {
    const formChanged = hasFormChanged(formData, initialValuesRef.current);
    const attachmentsChanged = hasAttachmentsChanged();
    const docsChanged = hasDocumentsChanged();
    
    if (formChanged || attachmentsChanged || docsChanged) {
      if (window.confirm("Are you sure you want to lose all unsaved progress?")) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleFieldChange = (name: string, value: any) => {
    const updated = { ...formData, [name]: value };
    setFormData(updated);
  };

  // Helper function to match country from API to our countries list
  const matchCountry = (apiCountry: string | undefined): string | undefined => {
    if (!apiCountry) return undefined;
    
    // Try exact match first
    const exactMatch = countries.find(c => c.toLowerCase() === apiCountry.toLowerCase());
    if (exactMatch) return exactMatch;
    
    // Try common variations
    const countryVariations: Record<string, string> = {
      'us': 'United States',
      'usa': 'United States',
      'united states of america': 'United States',
      'uk': 'United Kingdom',
      'united kingdom of great britain and northern ireland': 'United Kingdom',
      'uae': 'United Arab Emirates',
      'south korea': 'South Korea',
      'north korea': 'North Korea',
      'russia': 'Russia',
      'russian federation': 'Russia',
    };
    
    const normalized = apiCountry.toLowerCase().trim();
    if (countryVariations[normalized]) {
      return countryVariations[normalized];
    }
    
    // Try partial match (e.g., "United States" matches "United States of America")
    const partialMatch = countries.find(c => 
      c.toLowerCase().includes(normalized) || normalized.includes(c.toLowerCase())
    );
    if (partialMatch) return partialMatch;
    
    return undefined;
  };

  const handleAddressChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      address: value
    }));
  };

  const handleAddressSelect = (selection: AddressSelection) => {
    const matchedCountry = matchCountry(selection.country);
    setFormData(prev => ({
      ...prev,
      address: selection.addressLine1,
      city: selection.city || prev.city,
      state: selection.state || prev.state,
      zip_code: selection.postalCode || prev.zip_code,
      ...(matchedCountry && { country: matchedCountry }),
    }));
  };

  // Document management functions
  const addDocument = () => {
    const newDocument: VendorDocument = {
      id: Date.now().toString(),
      name: '',
      file: null,
      hasExpiry: false,
      expiryDate: ''
    };
    setDocuments([...documents, newDocument]);
  };

  const removeDocument = (id: string) => {
    setDocuments(documents.filter(doc => doc.id !== id));
  };

  const updateDocument = (id: string, updates: Partial<VendorDocument>) => {
    setDocuments(documents.map(doc => 
      doc.id === id ? { ...doc, ...updates } : doc
    ));
  };

  const handleEditExistingDocument = (document: VendorDocument) => {
    const editableDoc: VendorDocument = {
      id: document.id,
      name: document.name,
      file: null,
      hasExpiry: document.hasExpiry,
      expiryDate: document.expiryDate,
      uploadedFileId: document.uploadedFileId,
      fileUrl: document.fileUrl,
      file_path: document.file_path,
      isExisting: false
    };
    
    setDocuments(documents.map(doc => 
      doc.id === document.id ? editableDoc : doc
    ));
  };

  const handleDeleteExistingDocument = async (documentId: string) => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }

    if (!user?.id) return;

    try {
      const success = await deleteVendorDocument(documentId, user.id);
      if (success) {
        setDocuments(documents.filter(doc => doc.id !== documentId));
      } else {
        setError('Failed to delete document. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      setError('Failed to delete document. Please try again.');
    }
  };

  // Documents come back from fetchVendorDocuments already carrying a fresh
  // signed URL.
  const openDocument = (document: VendorDocument) => {
    if (document.fileUrl) window.open(document.fileUrl, '_blank');
  };

  const handleSubmit = async (data: Record<string, any>, { invite = false }: { invite?: boolean } = {}) => {
    // Validate required fields
    // A vendor is identified by its company: the contact person may be unknown or change
    if (!data.company_name?.trim() || !data.email?.trim()) {
      setError('Company name and email are required');
      return;
    }

    // Validate email format
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailRegex.test(data.email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Convert is_active back to boolean
      const submitData = {
        ...data,
        is_active: data.is_active === 'active',
      };

      // Save vendor first and get the vendor ID
      const vendorId = await onSave(submitData);

      // Save documents after vendor is created/updated
      // Use the returned vendor ID (for new vendors) or existing vendor ID
      const vendorIdToUse = vendorId || vendor?.id;
      
      if (vendorIdToUse && companyId && user?.id) {
        // Save documents that need to be uploaded or updated
        for (const document of documents.filter(doc => !doc.isExisting)) {
          if (document.uploadedFileId) {
            // Update existing document
            await updateVendorDocument(document.uploadedFileId, document, companyId, user.id);
          } else if (document.file && document.name.trim()) {
            // Upload new document
            await uploadVendorDocument(vendorIdToUse, document, companyId, user.id);
          }
        }
      }

      if (invite) {
        // the contact is saved; now start the commercial flow (never reported as sent unless the email really went out)
        let outcome: InvitationState | null = null;
        let failure: unknown = null;
        try {
          if (!vendorIdToUse) throw new Error('The contact has no id yet');
          outcome = await sendCommercialInvitation(vendorIdToUse);
        } catch (inviteError) {
          failure = inviteError;
        }
        onInvitation?.(noticeAfterSend(outcome, failure, data.company_name?.trim() || data.email));
      }

      onClose();
    } catch (err: any) {
      console.error('Error saving vendor:', err);
      setError(err.message || 'Failed to save vendor');
    } finally {
      setIsSubmitting(false);
    }
  };


  const { codes } = useCostCodes();
  const costCodeOptions = useMemo(() => operationalCodes(codes, vendor?.cost_code_ids || []).map(c => ({ label: costCodeLabel(c), value: c.id })), [codes, vendor?.cost_code_ids]);

  const vendorFields = useMemo<GenericFormField[]>(() => [
    {
      name: 'first_name',
      label: 'First Name',
      type: 'text',
      row: 1,
      colSpan: 1,
      placeholder: 'Enter first name',
    },
    {
      name: 'last_name',
      label: 'Last Name',
      type: 'text',
      row: 1,
      colSpan: 1,
      placeholder: 'Enter last name',
    },
    {
      name: 'email',
      label: 'Email',
      type: 'email',
      required: true,
      row: 2,
      colSpan: 1,
      placeholder: 'Enter email',
    },
    {
      name: 'phone',
      label: 'Phone',
      type: 'text',
      row: 2,
      colSpan: 1,
      placeholder: 'Enter phone number',
    },
    {
      name: 'company_name',
      label: 'Company Name',
      type: 'text',
      required: true,
      row: 3,
      colSpan: 0.5,
      placeholder: 'Enter company name',
    },
    {
      name: 'is_active',
      label: 'Status',
      type: 'select',
      row: 3,
      colSpan: 0.5,
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' },
      ],
      placeholder: 'Select',
    },
    {
      name: 'address',
      label: 'Address',
      type: 'text',
      row: 4,
      colSpan: 2,
      placeholder: 'Enter street address',
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      row: 5,
      colSpan: 0.5,
      placeholder: 'Enter city',
    },
    {
      name: 'state',
      label: 'State',
      type: 'text',
      row: 5,
      colSpan: 0.5,
      placeholder: 'Enter state',
    },
    {
      name: 'country',
      label: 'Country',
      type: 'select',
      row: 5,
      colSpan: 0.5,
      options: countries.map(c => ({ label: c, value: c })),
      placeholder: 'Select',
    },
    {
      name: 'zip_code',
      label: 'ZIP Code',
      type: 'text',
      row: 5,
      colSpan: 0.5,
      placeholder: 'Enter ZIP code',
    },
    {
      name: 'cost_code_ids',
      label: 'Cost Codes',
      type: 'multiselect',
      row: 6,
      colSpan: 2,
      placeholder: 'Search cost codes',
      options: costCodeOptions,
    },
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      row: 7,
      colSpan: 2,
      placeholder: 'Enter any additional notes',
    },
  ], [costCodeOptions]);

  // Group fields by row
  const fieldsByRow = vendorFields.reduce((acc, field) => {
    const row = field.row || 999;
    if (!acc[row]) acc[row] = [];
    acc[row].push(field);
    return acc;
  }, {} as Record<number, GenericFormField[]>);

  const sortedRows = Object.keys(fieldsByRow).sort((a, b) => Number(a) - Number(b));

  // Helper to render a field
  const renderField = (field: GenericFormField) => {
    const fieldValue = (formData as Record<string, any>)[field.name] || '';
    if (field.type === 'select') {
      return (
        <GenericSelect
          value={String(fieldValue)}
          onValueChange={(value) => handleFieldChange(field.name, value)}
          disabled={field.disabled}
          required={field.required}
          placeholder={field.placeholder}
          contentClassName={field.name === 'country' ? "max-h-[18.75rem] overflow-y-auto" : ""}
          options={field.options || []}
        />
      );
    } else {
      return (
        <Input
          id={field.name}
          type={field.type}
          value={String(fieldValue)}
          onChange={(e) => handleFieldChange(field.name, e.target.value)}
          placeholder={field.placeholder}
          className="font-form-input generic-form-field"
          disabled={field.disabled}
        />
      );
    }
  };


  const customLayout = (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit(formData);
      }}
      className="generic-form-container"
      style={{ maxHeight: 'var(--form-max-height-default)' }}
    >
      {/* Header */}
      <div 
        className={cn("generic-form-header", "generic-form-header-default")}
        style={{ backgroundColor: colors.formHeaderFooter }}
      >
        <h2 className="font-form-title">
          {vendor ? 'Edit Vendor' : 'Create Vendor'}
        </h2>
        <button
          type="button"
          onClick={handleCloseAttempt}
          className="generic-close-button"
        >
          <svg className="generic-close-button-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Body */}
      <div className={cn("generic-form-body", "generic-form-body-default")}>
        {error && (
          <div className="generic-form-error">
            {error}
          </div>
        )}
        <div className="generic-form-body-spacing-default">
          {/* Row 1: First Name, Last Name */}
          <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--field-gap-default)' }}>
            {fieldsByRow[1]?.map((field) => (
              <div key={field.name} className="space-y-2">
                <Label htmlFor={field.name} className="font-form-label">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </Label>
                {renderField(field)}
              </div>
            ))}
          </div>

          {/* Row 2: Email, Phone */}
          <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--field-gap-default)' }}>
            {fieldsByRow[2]?.map((field) => (
              <div key={field.name} className="space-y-2">
                <Label htmlFor={field.name} className="font-form-label">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </Label>
                {renderField(field)}
              </div>
            ))}
          </div>

          {/* Row 3: Company Name, Status */}
          <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--field-gap-default)' }}>
            {fieldsByRow[3]?.map((field) => (
              <div key={field.name} className="space-y-2">
                <Label htmlFor={field.name} className="font-form-label">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </Label>
                {renderField(field)}
              </div>
            ))}
          </div>

          {/* Row 4: Address (full width) with AddressAutocomplete */}
          <div className="space-y-2">
            <Label htmlFor="address" className="font-form-label">
              Address
            </Label>
            <AddressAutocomplete
              value={formData.address || ''}
              onChange={handleAddressChange}
              onSelectSuggestion={handleAddressSelect}
              placeholder="Start typing address"
              inputClassName="w-full"
              initialSelected={Boolean(vendor)}
            />
          </div>

          {/* Row 5: City, State, Country, ZIP Code (1/4 width each) */}
          <div className="grid grid-cols-1 md:grid-cols-4" style={{ gap: 'var(--field-gap-default)' }}>
            {fieldsByRow[5]?.map((field) => (
              <div key={field.name} className="space-y-2">
                <Label htmlFor={field.name} className="font-form-label">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </Label>
                {renderField(field)}
              </div>
            ))}
          </div>

          {/* Row 6: Notes */}
          <div className="space-y-2">
            {fieldsByRow[6]?.map((field) => (
              <div key={field.name} className="space-y-2">
                <Label htmlFor={field.name} className="font-form-label">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </Label>
                <Textarea
                  id={field.name}
                  value={String((formData as Record<string, any>)[field.name] || '')}
                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                  placeholder={field.placeholder}
                  disabled={field.disabled}
                />
              </div>
            ))}
          </div>

          {/* Documents Section (with expiry dates) */}
          <div className="space-y-4 pt-6 border-t">
            <div className="flex items-center justify-between">
              <Label className="font-form-subheader">Documents</Label>
              <StandardButton
                type="button"
                onClick={addDocument}
                customVariant="white"
                disabled={isSubmitting || isLoadingDocuments}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Document
              </StandardButton>
            </div>
            
            {/* Existing Documents */}
            {documents.filter(doc => doc.isExisting).length > 0 && (
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-600">Existing Documents</Label>
                {documents.filter(doc => doc.isExisting).map((document) => (
                  <div 
                    key={document.id} 
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleEditExistingDocument(document)}
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      <FileText className="h-5 w-5 text-black" />
                      <div className="flex-1">
                        <div 
                          className="font-medium text-black hover:underline cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDocument(document);
                          }}
                        >
                          {document.name}
                        </div>
                        {document.hasExpiry && document.expiryDate && (
                          <div className="text-sm text-gray-500">
                            Expires: {new Date(document.expiryDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StandardButton
                        type="button"
                        customVariant="white"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDocument(document);
                        }}
                        className="flex items-center gap-2"
                        style={{ border: 'none' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgb(var(--gray-200))'; // darker hover
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = colors.buttonTertiary;
                        }}
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </StandardButton>
                      <StandardButton
                        type="button"
                        customVariant="white"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteExistingDocument(document.id);
                        }}
                        className="p-0 h-6 w-6 min-w-0"
                        style={{ border: 'none' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgb(var(--gray-200))'; // darker hover
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = colors.buttonTertiary;
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </StandardButton>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* New/Edited Documents */}
            {documents.filter(doc => !doc.isExisting).length > 0 && (
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-600">
                  {documents.filter(doc => doc.isExisting && doc.uploadedFileId).length > 0 ? 'Edited Documents' : 'New Documents'}
                </Label>
                {documents.filter(doc => !doc.isExisting).map((document) => (
                  <div key={document.id} className="border rounded-lg p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Document</Label>
                      <StandardButton
                        type="button"
                        onClick={() => {
                          if (document.uploadedFileId) {
                            const originalDoc = documents.find(doc => doc.uploadedFileId === document.uploadedFileId && doc.isExisting);
                            if (originalDoc) {
                              setDocuments(documents.map(doc => 
                                doc.id === document.id ? { ...originalDoc, isExisting: true } : doc
                              ));
                            }
                          } else {
                            removeDocument(document.id);
                          }
                        }}
                        customVariant="white"
                        className="h-6 w-6 p-0 min-w-0"
                        style={{ border: 'none' }}
                      >
                        <X className="h-4 w-4" />
                      </StandardButton>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor={`doc-name-${document.id}`}>Name *</Label>
                        <Input
                          id={`doc-name-${document.id}`}
                          value={document.name}
                          onChange={(e) => updateDocument(document.id, { name: e.target.value })}
                          placeholder="e.g., COI, W9, License"
                          className="font-form-input generic-form-field"
                          disabled={isSubmitting}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="opacity-0">Upload</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            id={`doc-file-${document.id}`}
                            type="file"
                            onChange={(e) => updateDocument(document.id, { 
                              file: e.target.files ? e.target.files[0] : null 
                            })}
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            className="hidden"
                            disabled={isSubmitting}
                          />
                          <label
                            htmlFor={`doc-file-${document.id}`}
                            className={cn(
                              "cursor-pointer inline-flex items-center",
                              isSubmitting && "opacity-50 cursor-not-allowed"
                            )}
                          >
                            <SecondaryButton
                              type="button"
                              disabled={isSubmitting}
                              className="h-10"
                            >
                              Upload Files
                            </SecondaryButton>
                          </label>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`doc-expiry-${document.id}`}
                          checked={document.hasExpiry}
                          onChange={(e) => updateDocument(document.id, { hasExpiry: e.target.checked })}
                          className="rounded"
                          disabled={isSubmitting}
                        />
                        <Label htmlFor={`doc-expiry-${document.id}`}>Has expiry date</Label>
                      </div>
                      
                      {document.hasExpiry && (
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4" />
                          <Input
                            type="date"
                            value={document.expiryDate}
                            onChange={(e) => updateDocument(document.id, { expiryDate: e.target.value })}
                            className="w-40 font-form-input generic-form-field"
                            disabled={isSubmitting}
                          />
                        </div>
                      )}
                    </div>
                    
                    {document.file && (
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <FileText className="h-4 w-4" />
                        <span>Selected: {document.file.name}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {documents.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No documents added yet</p>
                <p className="text-sm">Click "Add Document" to upload COI, W9, licenses, or other required documents</p>
              </div>
            )}
          </div>

          {/* Attachments Section (general files without expiry) */}
          <div className="space-y-2 pt-6 border-t">
            <GenericAttachmentsSection
              attachments={attachments}
              pendingFiles={pendingFiles}
              onUpload={handleFileUpload}
              onDelete={vendor?.id ? handleFileDelete : undefined}
              onRemovePending={handleRemovePending}
              projectId={null} mode={vendor ? 'edit' : 'create'}
              disabled={isSubmitting || isLoadingAttachments}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div 
        className={cn("generic-form-footer", "generic-form-footer-default")}
        style={{ backgroundColor: colors.formHeaderFooter }}
      >
        <StandardButton
          onClick={handleCloseAttempt}
          type="button"
          customVariant="white"
        >
          Cancel
        </StandardButton>
        {vendor?.id && invitation && <span className="mr-auto text-sm text-gray-600" data-testid="invitation-status">{statusLabel(invitation)}</span>}
        <StandardButton
          type="button"
          customVariant="white"
          disabled={isSubmitting || invitation?.status === 'accepted'}
          onClick={() => handleSubmit(formData, { invite: true })}
        >
          {invitation && ['sent', 'failed', 'expired'].includes(invitation.status) ? 'Resend Invitation' : 'Send Invitation'}
        </StandardButton>
        <SecondaryButton
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </SecondaryButton>
      </div>
    </form>
  );

  return (
    <GenericForm
      title="Vendor"
      mode={vendor ? 'edit' : 'create'}
      fields={vendorFields}
      initialValues={formData}
      onSubmit={handleSubmit}
      onCancel={handleCloseAttempt}
      isOpen={isOpen}
      error={error}
      submitButtonText="Save"
      saving={isSubmitting}
      onFieldChange={handleFieldChange}
      customLayout={customLayout}
    />
  );
}

